import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { generateReview, submitReview, getManagerByCode } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import { Star, Copy, RefreshCw, Sparkles } from 'lucide-react';

function GenerateReview() {
  const { managerId } = useParams();
  const [review, setReview] = useState('');
  const [reviewId, setReviewId] = useState(null);
  const [googleLink, setGoogleLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [managerName, setManagerName] = useState('');
  const { isMobile } = useResponsive();

  useEffect(() => {
    generateAIReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateAIReview = async () => {
    setLoading(true);
    setError('');
    setCopied(false);
    setReview('');
    setReviewId(null);
    try {
      // Get manager info
      const { data: managerData } = await getManagerByCode(managerId);
      if (!managerData.business_name || !managerData.manager_name) {
        throw new Error('Business or manager information is missing.');
      }
      setGoogleLink(managerData.google_review_link || '');
      setBusinessName(managerData.business_name);
      setManagerName(managerData.manager_name);

      // Generate review
      const { data } = await generateReview({ managerCode: managerId });
      if (!data.reviewText) {
        throw new Error('The review could not be generated. Please try again.');
      }
      setReview(data.reviewText);
      setReviewId(data.reviewId);
    } catch (requestError) {
      console.error('Failed to generate review:', requestError);
      setError(
        requestError.response?.data?.error ||
        requestError.message ||
        'Unable to load the review. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAndRedirect = async () => {
    // Copy review to clipboard
    try {
      await navigator.clipboard.writeText(review);
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
    
    // Mark as submitted
    if (reviewId) {
      try {
        await submitReview({ reviewId, managerCode: managerId });
      } catch (error) {
        console.error('Failed to mark review as submitted:', error);
      }
    }
    
    // Open Google Reviews write form directly in same tab
    setTimeout(() => {
      if (googleLink) {
        // Redirect to Google Reviews form in same window
        window.location.href = googleLink;
      } else {
        // Fallback to Google Maps
        window.location.href = 'https://www.google.com/maps';
      }
    }, 500);
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8ecf1 0%, #f5f7fa 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '16px' : '20px'
    }}>
      <div style={{ 
        maxWidth: '600px',
        width: '100%',
        background: 'white',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '32px 24px' : '48px 40px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {/* Business Icon */}
        <div style={{
          width: isMobile ? '80px' : '100px',
          height: isMobile ? '80px' : '100px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: isMobile ? '40px' : '48px',
          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
        }}>
          <Sparkles size={isMobile ? 40 : 48} color="white" />
        </div>

        {!loading && !error && (
          <>
            {/* Business Name */}
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '700',
              color: '#2c3e50',
              marginBottom: '12px',
              lineHeight: '1.2'
            }}>
              {businessName}
            </h1>

            {/* Subtitle */}
            <p style={{ 
              fontSize: isMobile ? '14px' : '16px',
              color: '#7f8c8d',
              marginBottom: isMobile ? '24px' : '32px',
              fontWeight: '500'
            }}>
              Thanks for choosing us! Please share your experience.
            </p>

            {/* Manager Info */}
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              padding: isMobile ? '16px' : '20px',
              borderRadius: isMobile ? '12px' : '16px',
              marginBottom: isMobile ? '24px' : '32px',
              border: '1px solid #e8ecf1'
            }}>
              <p style={{ 
                fontSize: isMobile ? '13px' : '14px',
                color: '#7f8c8d',
                marginBottom: '8px',
                fontWeight: '600'
              }}>
                Personally served by
              </p>
              <p style={{ 
                fontSize: isMobile ? '18px' : '20px',
                color: '#667eea',
                fontWeight: '700',
                margin: 0
              }}>
                {managerName}
              </p>
            </div>
          </>
        )}

        {loading ? (
          <div style={{ padding: isMobile ? '40px 20px' : '60px 20px' }}>
            <div style={{ 
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}></div>
            <p style={{ 
              color: '#7f8c8d', 
              fontSize: isMobile ? '14px' : '15px', 
              fontWeight: '500' 
            }}>
              Generating your personalized review...
            </p>
          </div>
        ) : error ? (
          <div style={{ padding: isMobile ? '24px 12px' : '32px 20px' }} role="alert">
            <p style={{ color: '#b42318', marginBottom: '20px' }}>{error}</p>
            <button
              className="btn"
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: isMobile ? '16px' : '18px',
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '700',
                border: 'none'
              }}
              onClick={generateAIReview}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* Star Rating */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? '6px' : '8px',
              marginBottom: isMobile ? '24px' : '32px'
            }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star}
                  size={isMobile ? 32 : 40}
                  fill="#fbbf24"
                  color="#fbbf24"
                  style={{ 
                    filter: 'drop-shadow(0 2px 4px rgba(251, 191, 36, 0.3))'
                  }}
                />
              ))}
            </div>

            {/* Review Text */}
            <div style={{ 
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              padding: isMobile ? '20px' : '28px',
              borderRadius: isMobile ? '12px' : '16px',
              marginBottom: isMobile ? '24px' : '32px',
              minHeight: isMobile ? '120px' : '140px',
              border: '1px solid #e8ecf1',
              textAlign: 'left'
            }}>
              <p style={{ 
                lineHeight: '1.8',
                color: '#2c3e50',
                fontSize: isMobile ? '14px' : '15px',
                margin: 0,
                fontWeight: '500'
              }}>
                {review}
              </p>
            </div>

            {/* Copy & Continue Button */}
            <button 
              className="btn"
              style={{ 
                width: '100%',
                background: copied ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: isMobile ? '16px' : '18px',
                fontSize: isMobile ? '15px' : '16px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '10px' : '12px',
                border: 'none',
                marginBottom: '16px',
                boxShadow: copied ? '0 8px 24px rgba(17, 153, 142, 0.3)' : '0 8px 24px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s'
              }}
              onClick={handleCopyAndRedirect}
              disabled={copied}
            >
              <Copy size={isMobile ? 18 : 20} />
              <span>{copied ? '✓ Copied! Opening Google Reviews...' : 'Copy Review & Continue to Google'}</span>
            </button>

            {/* Helper Text */}
            <p style={{ 
              fontSize: isMobile ? '12px' : '13px',
              color: '#95a5a6',
              marginBottom: isMobile ? '20px' : '24px',
              fontWeight: '500',
              lineHeight: '1.6'
            }}>
              Your review will be copied to clipboard and Google Reviews will open automatically
            </p>

            {/* Regenerate Button */}
            <button 
              className="btn"
              style={{ 
                width: '100%',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: '#495057',
                padding: '14px',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                border: '2px solid #e8ecf1'
              }}
              onClick={generateAIReview}
              disabled={loading}
            >
              <RefreshCw size={18} />
              <span>Generate New Review</span>
            </button>
          </>
        )}
      </div>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default GenerateReview;
